import { QuantitySelectorUpdateEvent } from '@theme/events';

class SizeQuantitySelector extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('input', this.#handleInput);
    this.addEventListener('blur', this.#handleBlur, true);
    this.#syncState();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('input', this.#handleInput);
    this.removeEventListener('blur', this.#handleBlur, true);
  }

  /** @returns {Array<{variantId: string, quantity: number}>} */
  getItems() {
    return this.#inputs
      .map((input) => {
        this.#normalizeInput(input);
        return {
          variantId: input.dataset.variantId || '',
          quantity: this.#getInputQuantity(input),
        };
      })
      .filter((item) => item.variantId && item.quantity > 0);
  }

  /** @returns {number} */
  getTotalQuantity() {
    return this.#inputs.reduce((total, input) => total + this.#getInputQuantity(input), 0);
  }

  resetQuantities() {
    for (const input of this.#inputs) {
      input.value = '0';
    }

    this.#syncState();
  }

  /** @param {Array<{variant_id: number, quantity: number}>} cartItems */
  setCartQuantities(cartItems) {
    for (const input of this.#inputs) {
      const cartItem = cartItems.find((item) => String(item.variant_id) === String(input.dataset.variantId));
      input.dataset.cartQuantity = String(cartItem?.quantity || 0);
    }

    this.#syncState();
  }

  get #inputs() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('[data-size-quantity-input]')]);
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const button = event.target instanceof HTMLElement ? event.target.closest('[data-size-quantity-adjust]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const card = button.closest('[data-size-quantity-card]');
    const input = card?.querySelector('[data-size-quantity-input]');
    if (!(input instanceof HTMLInputElement)) return;

    const direction = Number(button.dataset.sizeQuantityAdjust) || 0;
    this.#adjustInput(input, direction);
  };

  /** @param {Event} event */
  #handleInput = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-size-quantity-input]')) return;

    this.#syncState();
  };

  /** @param {Event} event */
  #handleBlur = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-size-quantity-input]')) return;

    this.#normalizeInput(event.target);
    this.#syncState();
  };

  /**
   * @param {HTMLInputElement} input
   * @param {number} direction
   */
  #adjustInput(input, direction) {
    const current = this.#getInputQuantity(input);
    const minAdd = this.#getMinAdd(input);
    const step = this.#getStep(input);
    const effectiveMax = this.#getEffectiveMax(input);
    let nextQuantity = current;

    if (direction > 0) {
      nextQuantity = current === 0 ? minAdd : current + step;
    } else if (direction < 0) {
      nextQuantity = current <= minAdd ? 0 : current - step;
    }

    if (effectiveMax !== null) {
      nextQuantity = Math.min(nextQuantity, effectiveMax);
    }

    input.value = String(Math.max(0, nextQuantity));
    this.#syncState();
  }

  /** @param {HTMLInputElement} input */
  #normalizeInput(input) {
    let quantity = this.#getInputQuantity(input);
    const minAdd = this.#getMinAdd(input);
    const step = this.#getStep(input);
    const effectiveMax = this.#getEffectiveMax(input);

    if (quantity <= 0) {
      input.value = '0';
      return;
    }

    quantity = Math.max(quantity, minAdd);

    if ((quantity - minAdd) % step !== 0) {
      quantity = minAdd + Math.floor((quantity - minAdd) / step) * step;
    }

    if (effectiveMax !== null) {
      quantity = Math.min(quantity, effectiveMax);
    }

    input.value = String(Math.max(0, quantity));
  }

  #syncState() {
    for (const input of this.#inputs) {
      const card = input.closest('[data-size-quantity-card]');
      const quantity = this.#getInputQuantity(input);
      const effectiveMax = this.#getEffectiveMax(input);
      const minusButton = card?.querySelector('[data-size-quantity-adjust="-1"]');
      const plusButton = card?.querySelector('[data-size-quantity-adjust="1"]');

      if (card instanceof HTMLElement) {
        card.dataset.hasQuantity = quantity > 0 ? 'true' : 'false';
      }

      if (minusButton instanceof HTMLButtonElement) {
        minusButton.disabled = input.disabled || quantity <= 0;
      }

      if (plusButton instanceof HTMLButtonElement) {
        plusButton.disabled = input.disabled || (effectiveMax !== null && quantity >= effectiveMax);
      }
    }

    this.#updateAddButton();
    this.dispatchEvent(new QuantitySelectorUpdateEvent(this.getTotalQuantity()));
  }

  #updateAddButton() {
    const productForm = this.closest('product-form-component');
    const button = productForm?.querySelector('[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement)) return;

    const total = this.getTotalQuantity();
    const textElement = button.querySelector('.add-to-cart-text__content span span');
    const hasAvailableSize = this.#inputs.some((input) => !input.disabled && this.#getEffectiveMax(input) !== 0);

    if (textElement) {
      if (total === 1) {
        textElement.textContent = this.dataset.addOneText || this.dataset.defaultAddToCartText || '';
      } else if (total > 1) {
        textElement.textContent = (this.dataset.addManyText || '').replace('{count}', String(total));
      } else {
        textElement.textContent = this.dataset.defaultAddToCartText || '';
      }
    }

    button.disabled = !hasAvailableSize || total === 0;
    this.#syncStickyButton(productForm, button.disabled);
  }

  /**
   * @param {Element | null | undefined} productForm
   * @param {boolean} disabled
   */
  #syncStickyButton(productForm, disabled) {
    const stickyButton = productForm
      ?.closest('.shopify-section')
      ?.querySelector('sticky-add-to-cart [ref="addToCartButton"]');

    if (stickyButton instanceof HTMLButtonElement) {
      stickyButton.disabled = disabled;
    }
  }

  /** @param {HTMLInputElement} input */
  #getInputQuantity(input) {
    return Math.max(0, Number.parseInt(input.value, 10) || 0);
  }

  /** @param {HTMLInputElement} input */
  #getMinAdd(input) {
    return Math.max(1, Number.parseInt(input.dataset.minAdd || '1', 10) || 1);
  }

  /** @param {HTMLInputElement} input */
  #getStep(input) {
    return Math.max(1, Number.parseInt(input.step || '1', 10) || 1);
  }

  /** @param {HTMLInputElement} input */
  #getEffectiveMax(input) {
    if (!input.max) return null;

    const max = Number.parseInt(input.max, 10);
    const cartQuantity = Number.parseInt(input.dataset.cartQuantity || '0', 10) || 0;
    if (Number.isNaN(max)) return null;

    return Math.max(0, max - cartQuantity);
  }
}

if (!customElements.get('size-quantity-selector')) {
  customElements.define('size-quantity-selector', SizeQuantitySelector);
}
